<template lang="pug">
#GameList.flex.justify-center.w-full.h-full.min-w-fit(:data-game-entry="room.name?.['en-US']")
  .noData.flex.w-full.h-full(v-show="shouldShowNoData")
    NoData(:isFavorite="(room.name?.['en-US'] || '').includes('Favorite')")

  //- 使用單一 computed 判斷佈局類型，減少條件判斷次數
  template(v-if="layoutType === 'jackpot-game'")
    .jackpot-game.flex.items-center.w-full(class="portrait:flex-col")
      JackpotCard.jackpot-info(class="portrait:hidden", :key="room.categoryId", :jackpot="matchJackpot(room)", :enterAvailable="false")
      .game-list.flex-1.py-3.px-2.grid.grid-rows-2.grid-flow-col.auto-cols-max.gap-x-4.items-center.w-full(, :class="[{ 'sm:gap-y-6': showGameName }]", class="jackpotGames gap-y-2 sm:gap-y-4 landscape:ms-1 portrait:grid-rows-none portrait:auto-cols-auto portrait:grid-cols-2 portrait:grid-flow-row portrait:auto-rows-max portrait:gap-4 portrait:items-center")
        JackpotCard.jackpot-info.row-span-2(class="landscape:hidden", :key="room.categoryId", :jackpot="matchJackpot(room)", :enterAvailable="false")
        template(v-for="(item, index) in list", :key="item.gameCode || index")
          BaseGameCard(v-if="item.gameCode", :game="item", :gtmLocation="room.name ? room.name['en-US'] : ''", :gtmLocationId="room.categoryId", :isFavorite="hasFavorite(item.gameCode)", :showMaintenance="true", :extraInfo="extraInfo(item.gameCode)", @add="addFavorite", @remove="removeFavorite")

  template(v-else-if="layoutType === 'jackpot-list'")
    .jackpot-list.grid.grid-rows-1.grid-flow-col.auto-cols-max.gap-x-4.items-center.justify-items-center(class="gap-y-2 sm:gap-y-4 landscape:pe-4 portrait:grid-rows-none portrait:auto-cols-auto portrait:grid-cols-2 portrait:grid-flow-row portrait:auto-rows-max portrait:gap-4 portrait:items-center portrait:w-full landscape:pe-6", @dragscrollstart="onDragStart", @click.capture="onDragClick")
      template(v-if="sortedJackpotList.length > JACKPOT_MINIMUM")
        JackpotCard.jackpot-info(v-for="(item, index) in sortedJackpotList", :key="`jackpot-${item.jackpotId}`", :jackpot="matchJackpot(item)", :parent="room", @enter="enter", @finish="handleJackpotFinish")
      template(v-else)
        template(v-for="(n, index) in JACKPOT_MINIMUM", :key="sortedJackpotList[index] ? `jackpot-${sortedJackpotList[index].jackpotId}` : `jackpot-empty${n}`")
          JackpotCard.jackpot-info(v-if="sortedJackpotList[index]", :key="`jackpot-${sortedJackpotList[index].jackpotId}`", :jackpot="matchJackpot(sortedJackpotList[index])", :parent="room", @enter="enter", @finish="handleJackpotFinish")
          JackpotCard.jackpot-info(v-else, :jackpot="DEFAULT_JACKPOT", :key="`jackpot-fake-${n}`")

  template(v-else-if="layoutType === 'game-list'")
    .game-list.grid.grid-rows-2.grid-flow-col.auto-cols-max.gap-x-4.items-center.w-full(:class="[{ 'sm:gap-y-6': showGameName }]", class="gap-y-2 sm:gap-y-4 portrait:grid-rows-none portrait:auto-cols-auto portrait:grid-cols-2 portrait:grid-flow-row portrait:auto-rows-max portrait:gap-3 portrait:items-start")
      template(v-for="(item, index) in list", :key="item.gameCode || item.id || index")
        BaseGameCard(v-if="item.gameCode", :game="item", :gtmLocation="room.name ? room.name['en-US'] : ''", :gtmLocationId="room.categoryId", :isFavorite="hasFavorite(item.gameCode)", :showMaintenance="true", :extraInfo="extraInfo(item.gameCode)", @add="addFavorite", @remove="removeFavorite")
        BaseGameRoom(v-else, :room="item", :parent="room", @enter="enter")
  .game-list-loading.row-span-full.col-auto(key="loading", :class="['fixed landscape:xl:pe-[4.4rem] portrait:start-[50%] portrait:bottom-[5%]', { nodata: !list.length }, { hidden: !isLoadingNextPage }]")
    GameLoading(:fullScreen="false", :localImg="true", imgClass="w-12 h-12")
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'

import { useNuxtApp, useRuntimeConfig, useHead, useRoute } from '#app'
import { useI18n } from '#imports'
import { useAuthToken } from '@/composables/auth/useAuthToken'
import { useGameFavorite } from '@/composables/game/useGameFavorite'
import { useUserInfo } from '@/composables/user/useUserInfo'
import { useMainStore } from '@/stores/main'
import * as constant from '@/utils/constants'
import { toLocaleCodes } from '@/utils/i18nLocales'
import { AUTH_PANEL_SUB_ROUTES } from '@/utils/seo'
import { isTrue } from '@/utils/siteConfigChecker'
import { normalizeBasePath } from '@/utils/urlModal'

const props = defineProps({
  menu: Object,
  isLoadingNextPage: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['isJackpot'])

const { $analytics } = useNuxtApp()

const { gameInformationSummaryEnable, displayGameNameUnderIcon } = useSite()

const store = useMainStore()

const config = useRuntimeConfig()

const { isLoggedIn } = useAuthToken()

const { locale, locales } = useI18n()
const route = useRoute()

const { selectMenuItem, pushHistory, menuItemStack, historyStack } = useSideMenu()

const { room, content: gameContent, addition, isLastPage, gameStatus, load, setRoom, resetContent } = useGame()

const { userInfo } = useUserInfo({ autoInit: false })

const { hasFavorite, fetchFavorites, addFavorite, removeFavorite, clearFavorites } = useGameFavorite()

// ===== 常量定義 =====
const JACKPOT_MINIMUM = 4
const CURACAO_LIST = Object.freeze([
  Object.freeze({
    gameCode: '0937287b',
    providerId: 'JDB',
    mappingId: '2',
    gameTypeId: '7',
    gameId: '7003',
    isGame: true,
    isHot: false,
    isNew: false,
    name: { 'en-US': '财神捕鱼' },
  }),
  Object.freeze({
    gameCode: '0968ede2',
    providerId: 'JDB',
    mappingId: '2',
    gameTypeId: '7',
    gameId: '7004',
    isGame: true,
    isHot: false,
    isNew: false,
    name: { 'en-US': '五龙捕鱼' },
  }),
  Object.freeze({
    gameCode: '09d07999',
    providerId: 'JDB',
    mappingId: '1',
    gameTypeId: '0',
    gameId: '14026',
    isGame: true,
    isHot: false,
    isNew: false,
    name: { 'en-US': '发大财' },
  }),
  Object.freeze({
    gameCode: '09c18848',
    providerId: 'JDB',
    mappingId: '1',
    gameTypeId: '0',
    gameId: '8049',
    isGame: true,
    isHot: false,
    isNew: false,
    name: { 'en-US': 'Flirting Scholar Tang II' },
  }),
  Object.freeze({
    gameCode: '09fde47e',
    providerId: 'JDB',
    mappingId: '1',
    gameTypeId: '0',
    gameId: '8047',
    isGame: true,
    isHot: false,
    isNew: false,
    name: { 'en-US': 'Winning Mask II' },
  }),
])

const DEFAULT_JACKPOT = Object.freeze({
  id: '0',
  startTime: 0,
  endTime: 0,
  rewardConfig: Object.freeze({
    defaultAmount: 0,
  }),
  totalReward: 0,
})

// ===== 響應式狀態 =====
let dragged = false
let dragTimeout = null

const getJackpotList = computed(() => store.getJackpotList)

const isDesktop = computed(() => store.isDesktop)

const showGameName = computed(() => isTrue(displayGameNameUnderIcon.value || ''))

// 使用 shallowRef 減少深層響應式追蹤
const isCuracaoScope = computed(() => userInfo.value?.scope === 'curacao')

const content = computed(() => {
  // 提早返回，避免不必要的物件建立
  if (!isCuracaoScope.value) return gameContent.value
  return { data: CURACAO_LIST }
})

const list = computed(() => content.value?.data || [])

// Jackpot 結束排序：當 JackpotCard 倒數到期時 emit('finish')，
// 將已結束的 Jackpot 卡片排到列表最後，避免佔據前排位置
// 切換房間時清空，避免舊的結束狀態影響新房間或重新開始的 jackpot
const finishedJackpotIds = ref(new Set())

watch(room, () => {
  finishedJackpotIds.value = new Set()
})

const handleJackpotFinish = (jackpotId) => {
  const updated = new Set(finishedJackpotIds.value)
  updated.add(jackpotId)
  finishedJackpotIds.value = updated
}

const sortedJackpotList = computed(() => {
  return [...list.value].sort((a, b) => {
    const aFinished = finishedJackpotIds.value.has(a.jackpotId)
    const bFinished = finishedJackpotIds.value.has(b.jackpotId)
    if (aFinished === bFinished) return 0
    return aFinished ? 1 : -1
  })
})

const isListLoading = computed(() => gameStatus.value === 'loading')

// const showNoData = computed(() => gameStatus.value === 'idle')

const pageTitle = computed(() => {
  return `${getSideHeaderTitle.value} - ${config.public.name}`
})

const getSideHeaderTitle = computed(() => {
  const currentLocale = locale.value
  const englishLocale = 'en-US'

  // 使用 map + join 取代手動字串串接
  const menuItem = menuItemStack.value
    .map((item) => {
      const { name = {}, nameI18n = {} } = item
      return name?.[currentLocale] || name?.[englishLocale] || nameI18n?.[currentLocale] || nameI18n?.[englishLocale] || name || ''
    })
    .filter(Boolean)
    .join(' | ')

  const backwardItems = historyStack.value
    .map((item) => item?.name?.[currentLocale] || item?.name?.[englishLocale] || '')
    .filter(Boolean)
    .join(' | ')

  const currentItem = room.value?.name?.[currentLocale] || room.value?.name?.[englishLocale] || ''

  // 簡化邏輯
  if (!backwardItems) return menuItem
  return currentItem ? `${backwardItems} | ${currentItem}` : backwardItems
})

/**
 * 優化 NoData 顯示判斷
 * 使用 computed 快取結果，避免重複計算
 */
const shouldShowNoData = computed(() => {
  return !isListLoading.value && !list.value.length && !!room.value && isLastPage.value
})

/**
 * 計算當前應該顯示的佈局類型
 */
const layoutType = computed(() => {
  const hasData = list.value.length > 0

  if (!hasData) return 'none'
  if (room.value?.isJackpot) return 'jackpot-game'
  if (list.value[0]?.isJackpot) return 'jackpot-list'
  return 'game-list'
})

// /guest/login、/guest/register 只是開窗子路由，畫面沿用父層 /guest 大廳，
// 但 title 必須由 layout 依 SEO 規則輸出（Guest | Login - 站名）。子元件的 useHead
// 後註冊、優先權高於 layout，故這兩條路由不設 title，避免蓋掉 layout 的結果。
// 規格：TI-15592（JT-1784793000）
const isAuthPanelSubRoute = computed(() => {
  return AUTH_PANEL_SUB_ROUTES.includes(normalizeBasePath(route.path, toLocaleCodes(locales)))
})

useHead(() => (isAuthPanelSubRoute.value ? {} : { title: pageTitle.value }))

/**
 * 建立 menu 索引 Map，將遞歸查找優化為 O(1) 查找
 * 時間複雜度：建立 O(n)，查找 O(1)
 */
const menuIndexMap = computed(() => {
  const map = new Map()

  const buildIndex = (items) => {
    if (!Array.isArray(items)) return
    items.forEach((item) => {
      if (item?.categoryId) {
        map.set(item.categoryId, item)
      }
      if (item.sub?.length > 0) {
        buildIndex(item.sub)
      }
    })
  }

  if (props.menu?.data) {
    buildIndex(props.menu.data)
  }

  return map
})

/**
 * 從 menu 中查找指定 categoryId 的項目
 * @param {string} categoryId - 分類 ID
 * @returns {Object|undefined} - 找到的 menu 項目
 */
const findInMenu = (categoryId) => {
  return menuIndexMap.value.get(categoryId)
}

const enter = (targetRoom) => {
  const hasMenu = findInMenu(targetRoom.categoryId)
  if (hasMenu) {
    // 確保 hasMenu 有 sub 屬性
    if (!hasMenu.sub) hasMenu.sub = []
    selectMenuItem(hasMenu)
  } else {
    // 將當前房間推入歷史堆疊
    const currentRoom = JSON.parse(JSON.stringify(room.value))
    pushHistory(currentRoom)
  }
  setRoom(targetRoom)
}

// ===== Map 索引優化 =====
/**
 * 建立 gameCode -> extraInfo 的 Map，提升查找效率
 * 使用 computed 確保只在 addition.value.data 變化時重建 Map
 * 時間複雜度：建立 O(n)，查找 O(1)
 */
const extraInfoMap = computed(() => {
  const data = addition.value?.data
  if (!Array.isArray(data) || data.length === 0) {
    return new Map()
  }

  // 使用 reduce 建立 Map，更簡潔且性能相同
  return data.reduce((map, item) => {
    if (item?.gameCode) {
      map.set(item.gameCode, item)
    }
    return map
  }, new Map())
})

/**
 * 查找函數：使用 Map 進行快速查找
 * @param {string} gameCode - 遊戲代碼
 * @returns {Object|undefined} - 遊戲額外資訊
 */
const extraInfo = (gameCode) => {
  if (!gameCode) return undefined
  return extraInfoMap.value.get(gameCode)
}

/**
 * 建立 jackpotId -> jackpot 的 Map，提升查找效率
 * 時間複雜度：建立 O(n)，查找 O(1)
 */
const jackpotMap = computed(() => {
  return new Map(getJackpotList.value.map((jackpot) => [jackpot.id, jackpot]))
})

/**
 * 匹配 Jackpot 資訊
 * @param {Object} item - 遊戲或房間項目
 * @returns {Object} - 合併後的項目（包含 jackpot 資訊）
 */
const matchJackpot = (item) => {
  if (!item?.jackpotId) return item
  const jackpot = jackpotMap.value.get(item.jackpotId)
  return jackpot ? { ...item, ...jackpot } : item
}

/**
 * 處理拖動開始事件
 */
const onDragStart = () => {
  clearTimeout(dragTimeout)
  dragged = false
  dragTimeout = setTimeout(() => {
    dragged = true
  }, 100)
}

/**
 * 處理拖動點擊事件
 */
const onDragClick = (e) => {
  if (dragged) {
    e.stopPropagation()
  }
  dragged = false
}

/**
 * 監聽 room 變化，追蹤分析事件
 * 需要監聽整個 room 物件以獲取 oldData 和 newData
 */
watch(room, (newRoom, oldRoom) => {
  if (oldRoom?.categoryId && newRoom?.categoryId && newRoom.categoryId !== oldRoom.categoryId) {
    $analytics.push({
      event: 'page_view',
      location: oldRoom?.name?.['en-US'],
      page_title: newRoom?.name?.['en-US'],
      window_type: 'page',
      action_source: 'click',
      component_name: 'GameList',
    })
  }
})

// 優化 content 監聽
watch(
  () => content.value?.data?.length,
  (length) => {
    if (length > 0) {
      emit('isJackpot', room.value?.isJackpot || list.value?.[0]?.isJackpot || false)
    }
  },
  { flush: 'post' }
)

/**
 * 監聽登入狀態變化
 * - 登入時：獲取收藏列表
 * - 登出時：清空收藏列表
 */
watch(
  isLoggedIn,
  (newLoggedIn, oldLoggedIn) => {
    if (newLoggedIn) {
      // 登入時獲取收藏列表
      fetchFavorites()
    } else if (oldLoggedIn !== undefined && !newLoggedIn) {
      // 登出時清空收藏列表（oldLoggedIn !== undefined 確保不是初始化）
      clearFavorites()
    }
  },
  { immediate: true }
)

/**
 * 組件卸載時清除遊戲資料
 * - 清除 content 資料
 * - 清除 addition 資料
 */
onUnmounted(() => {
  resetContent()
})
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

#GameList {
  padding-inline-end: env(safe-area-inset-right);
}

.game-list-loading {
  inset-inline-end: 0;
  top: 40%;
}

@media (min-width: 1024px) and (orientation: landscape) {
  .game-list-loading {
    top: auto;
    bottom: 3rem;
    inset-inline-end: auto;
    inset-inline-start: auto;
  }
}

@media (orientation: portrait) {
  .game-list-loading {
    inset-inline-end: initial;
    top: initial;
  }
}

.game-list-loading.nodata {
  @apply bottom-1/2 translate-y-1/2;
}

@media (max-width: 1024px) and (orientation: landscape) {
  .game-list-loading.nodata {
    inset-inline-end: auto;
    inset-inline-start: auto;
  }
}
.jackpot-info {
  height: 100%;
  width: auto;
  aspect-ratio: 44 / 69;
  max-width: 440px;
  max-height: 690px;
}

@media (orientation: portrait) {
  .jackpot-info {
    width: 100%;
    height: auto;
  }
}

.game-list {
  @apply pb-10 h-fit pe-[4.4rem];
}

.game-list.jackpotGames {
  @apply overflow-x-auto;
}

@media (max-width: 1023px) and (orientation: landscape) {
  .game-list {
    @apply h-full pb-2;
  }

  .game-list.jackpotGames {
    @apply w-0;
  }
}

@media (min-width: 768px) and (orientation: landscape) {
  .game-list {
    @apply grid-rows-2 grid-flow-col grid-cols-none;
  }
}

@media (min-width: 1024px) and (orientation: landscape) {
  .game-list {
    grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr));
    @apply grid-flow-row grid-rows-none h-fit pb-12 gap-x-[0.8rem];
  }

  .game-list.jackpotGames {
    height: 100%;
    align-content: flex-start;
    @apply overflow-y-auto;
  }
}

@media (min-width: 1920px) and (orientation: landscape) {
  .game-list {
    grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
  }
}

@media screen and (orientation: portrait) {
  .game-list {
    @apply pe-0;
  }
}

@media (min-width: 768px) and (orientation: portrait) {
  .game-list {
    @apply grid-cols-3 grid-flow-row grid-rows-none;
  }
}

@media (min-width: 1024px) and (orientation: portrait) {
  .game-list {
    @apply grid-cols-4;
  }
}

.jackpot-list {
  height: inherit;
  max-height: 690px;
}

@media (orientation: portrait) {
  .jackpot-list {
    @apply pe-0;
  }
}

@media (orientation: landscape) {
  .jackpot-list {
    @apply pe-[4.4rem];
  }
}

@media (min-width: 1024px) and (orientation: landscape) {
  .jackpot-list {
    @apply gap-x-[0.8rem];
    max-height: none;
  }
}
</style>
